import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { api } from "../lib/api";
import { OPTIONS, formatMoney, currencyForCountry } from "../lib/finance";
import {
  Trash2,
  Sparkles,
  ArrowRight,
  User,
  Wallet,
  LineChart,
  BellRing,
  CalendarCheck,
} from "lucide-react";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import BadgesGrid from "../components/BadgesGrid";
import ShareDialog from "../components/ShareDialog";
import ObjectiveCard from "../components/ObjectiveCard";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [recos, setRecos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reminderBusy, setReminderBusy] = useState(false);

  useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  const load = async () => {
    try {
      const { data } = await api.get("/recommendations");
      setRecos(data.items);
    } finally {
      setLoading(false);
    }
  };

  const currentMonth = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };

  const [checkinBusy, setCheckinBusy] = useState(false);
  const [coachBusy, setCoachBusy] = useState(false);

  const sendCoachTip = async () => {
    setCoachBusy(true);
    try {
      const { data } = await api.post("/coach/send-tip");
      if (data?.sent) {
        toast.success(t("coach_tip_sent"));
      } else {
        toast.error(t("coach_tip_failed"));
      }
    } catch {
      toast.error(t("coach_tip_failed"));
    } finally {
      setCoachBusy(false);
    }
  };
  const checkins = user?.checkins || [];
  const alreadyCheckedIn = checkins.includes(currentMonth());

  const doCheckin = async () => {
    setCheckinBusy(true);
    const before = new Set(user?.badges || []);
    try {
      const { data } = await api.post("/checkin", { month: currentMonth() });
      const updated = data?.user || {};
      const newBadges = (updated.badges || []).filter((b) => !before.has(b));
      await refresh();
      if (newBadges.length > 0) {
        celebrateBadges(newBadges, t);
      } else {
        toast.success(t("checkin_done"));
      }
    } catch {
      toast.error(t("err_generic"));
    } finally {
      setCheckinBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/recommendations/${id}`);
      setRecos((r) => r.filter((x) => x.id !== id));
      toast.success(lang === "fr" ? "Supprimé" : "Deleted");
    } catch {
      toast.error(t("err_generic"));
    }
  };

  const toggleReminder = async (checked) => {
    setReminderBusy(true);
    try {
      if (checked) {
        if (typeof Notification !== "undefined") {
          const perm = await Notification.requestPermission();
          if (perm !== "granted") {
            toast.error(t("reminder_permission_denied"));
            setReminderBusy(false);
            return;
          }
        }
      }
      await api.patch("/profile/reminder", { enabled: checked, day_of_month: 1 });
      await refresh();
      if (checked) toast.success(t("reminder_enabled"));
    } catch {
      toast.error(t("err_generic"));
    } finally {
      setReminderBusy(false);
    }
  };

  // Fire a friendly local notification when appropriate.
  useEffect(() => {
    if (!user?.reminder?.enabled) return;
    if (typeof Notification === "undefined" || Notification.permission !== "granted")
      return;
    const key = `sereno_last_reminder_${user.id}`;
    const now = new Date();
    const last = localStorage.getItem(key);
    const should =
      !last ||
      Math.abs(now - new Date(last)) > 25 * 24 * 60 * 60 * 1000; // ~monthly
    if (should) {
      try {
        new Notification("Sereno", {
          body:
            lang === "fr"
              ? `${user.name}, c'est le moment de mettre vos ${formatMoney(
                  user.profile?.monthly_savings || 50,
                  currencyForCountry(user.country || "FR"),
                  "fr"
                )} de côté ce mois-ci.`
              : `${user.name}, time to set aside your ${formatMoney(
                  user.profile?.monthly_savings || 50,
                  currencyForCountry(user.country || "FR"),
                  "en"
                )} for this month.`,
          icon: "/favicon.ico",
        });
        localStorage.setItem(key, now.toISOString());
      } catch {
        // ignore
      }
    }
  }, [user, lang]);

  if (!user) return null;
  const currency = currencyForCountry(user.country || "FR");
  const p = user.profile || {};

  return (
    <div className="mx-auto max-w-5xl px-6 py-12" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-base font-semibold uppercase tracking-widest text-[#A762B1]">
            {t("dashboard")}
          </p>
          <h1
            className="mt-2 font-serif text-4xl font-semibold text-[#101A42] sm:text-5xl"
            data-testid="dashboard-greeting"
          >
            {lang === "fr" ? `Bonjour ${user.name}` : `Hi ${user.name}`}
          </h1>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            onClick={() => navigate("/simulateur")}
            data-testid="dashboard-simulator-btn"
            className="min-h-[56px] rounded-full border-2 border-[#101A42] px-6 text-lg font-semibold text-[#101A42] hover:bg-[#101A42] hover:text-white"
          >
            {t("simulator")}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/quiz")}
            data-testid="dashboard-quiz-btn"
            className="min-h-[56px] rounded-full border-2 border-[#A762B1] px-6 text-lg font-semibold text-[#A762B1] hover:bg-[#A762B1] hover:text-white"
          >
            {t("quiz")}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <Button
            onClick={() => navigate("/onboarding")}
            data-testid="redo-tutorial-btn"
            className="min-h-[56px] rounded-full bg-[#101A42] px-6 text-lg font-semibold text-white hover:bg-[#1A2650]"
          >
            {t("redo_tutorial")}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <Button
            variant="outline"
            onClick={sendCoachTip}
            disabled={coachBusy}
            data-testid="coach-tip-btn"
            className="min-h-[56px] rounded-full border-2 border-[#E59D32] px-6 text-lg font-semibold text-[#8A6300] hover:bg-[#E59D32] hover:text-white"
          >
            {t("coach_send_tip")}
          </Button>
        </div>
      </div>

      <section
        className="mt-10 rounded-3xl border border-[#E8DFC7] bg-white p-6 sm:p-8"
        data-testid="profile-card"
      >
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#101A42]/10 text-[#101A42]">
            <User className="h-6 w-6" />
          </div>
          <h2 className="font-serif text-2xl font-semibold text-[#101A42] sm:text-3xl">
            {t("my_profile")}
          </h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <ProfileStat
            icon={Wallet}
            label={lang === "fr" ? "Revenus/mois" : "Income/mo"}
            value={p.monthly_income ? formatMoney(p.monthly_income, currency, lang) : "—"}
          />
          <ProfileStat
            icon={Wallet}
            label={lang === "fr" ? "Dépenses/mois" : "Expenses/mo"}
            value={
              p.monthly_expenses ? formatMoney(p.monthly_expenses, currency, lang) : "—"
            }
          />
          <ProfileStat
            icon={LineChart}
            label={lang === "fr" ? "Épargne/mois" : "Saving/mo"}
            value={
              p.monthly_savings ? formatMoney(p.monthly_savings, currency, lang) : "—"
            }
            accent
          />
          <ProfileStat
            icon={Sparkles}
            label={lang === "fr" ? "Profil" : "Profile"}
            value={p.risk_profile ? t(`risk_${p.risk_profile}`) : "—"}
          />
        </div>
      </section>

      <section className="mt-12">
        <div
          className="mb-8 flex flex-col gap-4 rounded-3xl border border-[#E8DFC7] bg-white p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8"
          data-testid="reminder-card"
        >
          <div className="flex items-start gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#A762B1]/15 text-[#A762B1]">
              <BellRing className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-serif text-xl font-semibold text-[#101A42] sm:text-2xl">
                {t("monthly_reminder")}
              </h3>
              <p className="mt-1 text-base leading-relaxed text-[#4A4A4A]">
                {t("monthly_reminder_desc")}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-base text-[#4A4A4A]">
              {user.reminder?.enabled ? t("reminder_enabled") : t("enable_reminder")}
            </span>
            <Switch
              data-testid="reminder-toggle"
              checked={!!user.reminder?.enabled}
              disabled={reminderBusy}
              onCheckedChange={toggleReminder}
            />
          </div>
        </div>

        <div className="mt-8">
          <ObjectiveCard />
        </div>

        <h2 className="mt-10 font-serif text-2xl font-semibold text-[#101A42] sm:text-3xl">
          {t("my_recos")}
        </h2>
        {loading ? (
          <p className="mt-6 text-lg text-[#4A4A4A]">…</p>
        ) : recos.length === 0 ? (
          <div
            data-testid="no-recos"
            className="mt-6 rounded-3xl border-2 border-dashed border-[#101A42]/25 p-10 text-center"
          >
            <p className="text-lg text-[#4A4A4A]">{t("no_recos")}</p>
            <Button
              onClick={() => navigate("/onboarding")}
              data-testid="empty-start-btn"
              className="mt-6 min-h-[56px] rounded-full bg-[#A762B1] px-6 text-lg font-semibold text-white hover:bg-[#B775C0]"
            >
              {t("start_now")}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        ) : (
          <ul className="mt-6 grid gap-5">
            {recos.map((r) => {
              const opt = OPTIONS[r.recommendation_id];
              const localized = opt?.[lang] || opt?.fr;
              return (
                <li
                  key={r.id}
                  data-testid={`reco-item-${r.id}`}
                  className="flex flex-col gap-4 rounded-2xl border border-[#E8DFC7] bg-white p-6 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <p className="font-serif text-2xl font-semibold text-[#101A42]">
                      {localized?.name || r.recommendation_id}
                    </p>
                    <p className="mt-1 text-base text-[#4A4A4A]">
                      {formatMoney(r.monthly_amount, currency, lang)}
                      {t("per_month")} • {r.horizon_years} {t("years")} →{" "}
                      <span className="font-semibold text-[#101A42]">
                        {formatMoney(r.projected_value, currency, lang)}
                      </span>
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <ShareDialog recommendationId={r.id} />
                    <Button
                      variant="outline"
                      onClick={() => remove(r.id)}
                      data-testid={`reco-delete-${r.id}`}
                      className="min-h-[48px] rounded-full border-2 border-[#A762B1] text-[#A762B1] hover:bg-[#A762B1] hover:text-white"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t("delete")}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="mt-14" data-testid="checkin-section">
        <div className="rounded-3xl border-2 border-[#A762B1]/25 bg-white p-6 sm:p-8">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="max-w-xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-[#A762B1]/10 px-3 py-1.5 text-sm font-semibold text-[#A762B1]">
                <CalendarCheck className="h-4 w-4" />
                {t("checkin_this_month")} — {currentMonth()}
              </div>
              <h2 className="font-serif text-2xl font-semibold text-[#101A42] sm:text-3xl">
                {t("checkin_title")}
              </h2>
              <p className="mt-2 text-lg leading-relaxed text-[#4A4A4A]">
                {t("checkin_desc")}
              </p>
            </div>
            <Button
              onClick={doCheckin}
              disabled={checkinBusy || alreadyCheckedIn}
              data-testid="checkin-btn"
              className={`min-h-[60px] rounded-full px-8 text-lg font-semibold ${
                alreadyCheckedIn
                  ? "bg-[#A762B1] text-white"
                  : "bg-[#A762B1] text-white hover:bg-[#B775C0]"
              }`}
            >
              {alreadyCheckedIn ? (
                <>
                  <CalendarCheck className="mr-2 h-5 w-5" />
                  {t("checkin_done")}
                </>
              ) : (
                t("checkin_btn")
              )}
            </Button>
          </div>
        </div>
      </section>

      <section className="mt-14" data-testid="badges-section">
        <h2 className="font-serif text-2xl font-semibold text-[#101A42] sm:text-3xl">
          {t("badges")}
        </h2>
        <div className="mt-6">
          <BadgesGrid earned={user.badges || []} />
        </div>
      </section>
    </div>
  );
}

function ProfileStat({ icon: Icon, label, value, accent }) {
  return (
    <div
      className={`rounded-2xl border p-5 ${
        accent ? "border-[#A762B1]/40 bg-[#A762B1]/5" : "border-[#E8DFC7] bg-[#FFF7E9]/60"
      }`}
    >
      <div className="flex items-center gap-2 text-base text-[#4A4A4A]">
        <Icon className="h-5 w-5" />
        {label}
      </div>
      <p className="mt-2 font-serif text-2xl font-semibold text-[#101A42]">{value}</p>
    </div>
  );
}

// Fires a confetti burst + toast for each newly unlocked badge.
function celebrateBadges(newBadges, t) {
  // Rich palette with gold & mauve for a more festive celebration
  const colors = [
    "#A762B1", // signature orange
    "#B775C0", // orange-light
    "#A762B1", // green
    "#101A42", // dark green
    "#E59D32", // gold ✨
    "#F4C77B", // pale gold
    "#A762B1", // mauve
    "#A78BFA", // pale mauve
  ];
  // Two symmetrical bursts for a satisfying celebration.
  const fire = () => {
    confetti({
      particleCount: 90,
      spread: 70,
      startVelocity: 45,
      origin: { x: 0.2, y: 0.7 },
      angle: 60,
      colors,
      scalar: 1.1,
    });
    confetti({
      particleCount: 90,
      spread: 70,
      startVelocity: 45,
      origin: { x: 0.8, y: 0.7 },
      angle: 120,
      colors,
      scalar: 1.1,
    });
  };
  fire();
  setTimeout(fire, 250);
  setTimeout(fire, 500);

  newBadges.forEach((key, i) => {
    setTimeout(() => {
      toast.success(`🏅 ${t("badge_unlocked")} — ${t(`badge_${key}`)}`, {
        description: t(`badge_${key}_desc`),
        duration: 6000,
      });
    }, i * 700);
  });
}
